// Shared formatting helpers for Harmony Pulse views.

// Org-wide display timezone. Set once from /overview so every viewer reads the
// same wall-clock times (HR in one place, a resource in another) instead of
// each browser's local zone. undefined => fall back to the browser's zone.
let DISPLAY_TZ;
export const setDisplayTimezone = (tz) => {
  // Validate ONCE here so a malformed zone (env typo, bad settings value) can't
  // reach Intl during render and throw RangeError. Invalid -> undefined (browser
  // zone), which every formatter already handles.
  if (!tz) { DISPLAY_TZ = undefined; return; }
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    DISPLAY_TZ = tz;
  } catch (_) {
    DISPLAY_TZ = undefined;
  }
};
export const displayTimezone = () => DISPLAY_TZ || null;
const withTz = (opts) => (DISPLAY_TZ ? { ...opts, timeZone: DISPLAY_TZ } : opts);

// Minutes (float) -> "7h 30m" / "45m" / "0m".
export const fmtMins = (m) => {
  const n = Math.max(0, Math.round(Number(m) || 0));
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (h && mm) return `${h}h ${mm}m`;
  if (h) return `${h}h`;
  return `${mm}m`;
};

// Minutes -> decimal hours string "7.5" (one dp, trims .0).
export const fmtHours = (m) => {
  const h = (Number(m) || 0) / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
};

// ISO timestamp -> "HH:mm" in the org timezone. Empty string for null/bad input.
export const fmtClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return d.toLocaleTimeString([], withTz(opts));
  } catch (_) {
    return d.toLocaleTimeString([], opts); // bad tz -> browser zone, never crash render
  }
};

// ISO -> "20 Jul, 14:03" in the org timezone.
export const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return d.toLocaleString([], withTz(opts));
  } catch (_) {
    return d.toLocaleString([], opts);
  }
};

// 'YYYY-MM-DD' -> "Mon 20 Jul". Falls back to the raw string.
export const fmtDay = (ymd) => {
  if (!ymd || ymd.length !== 10) return ymd || '';
  const d = new Date(ymd + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString([], { weekday: 'short', day: '2-digit', month: 'short' });
};

// Relative "just now / 5 min ago / 2 h ago / 20 Jul 14:03".
export const fmtRelative = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const diffMin = Math.floor((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h ago`;
  return fmtDateTime(iso);
};

// ISO -> decimal hour-of-day in the org timezone, e.g. 14:30 -> 14.5. null for
// bad input. Uses the org zone so the activity-by-hour chart bins by the same
// clock the times are rendered in.
export const hourOfDay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (!DISPLAY_TZ) return d.getHours() + d.getMinutes() / 60;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    let hh = Number((parts.find((p) => p.type === 'hour') || {}).value);
    const mm = Number((parts.find((p) => p.type === 'minute') || {}).value);
    if (Number.isNaN(hh)) return d.getHours() + d.getMinutes() / 60;
    if (hh === 24) hh = 0; // some engines emit '24' at midnight
    return hh + (Number.isNaN(mm) ? 0 : mm) / 60;
  } catch (_) {
    return d.getHours() + d.getMinutes() / 60;
  }
};

// Minute-of-day in the ORG timezone for an instant, measured from midnight of the
// day being rendered (`ymd`). May return < 0 (the instant is before this day) or
// > 1440 (after it) so callers can clamp at both ends.
//
// Why not hourOfDay()*60: that gives a wall-clock time with no DATE, so
// "yesterday 22:15" and "today 22:15" are indistinguishable. And deriving the far
// end by ADDING elapsed minutes to a wall-clock anchor mixes two scales — it is
// off by a minute whenever the start carries seconds, and off by a whole hour
// across a DST transition. Reading the zone's wall-clock components for BOTH ends
// keeps them in one scale and makes 23- and 25-hour days correct for free.
export const minuteOfDayIn = (iso, ymd) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let y, mo, day, hh, mm;
  if (!DISPLAY_TZ) {
    y = d.getFullYear(); mo = d.getMonth() + 1; day = d.getDate();
    hh = d.getHours(); mm = d.getMinutes();
  } else {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: DISPLAY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const g = (t) => Number((parts.find((p) => p.type === t) || {}).value);
      y = g('year'); mo = g('month'); day = g('day'); hh = g('hour'); mm = g('minute');
      if (hh === 24) hh = 0;                       // some engines emit '24' at midnight
    } catch (_) {
      y = d.getFullYear(); mo = d.getMonth() + 1; day = d.getDate();
      hh = d.getHours(); mm = d.getMinutes();
    }
  }
  if ([y, mo, day, hh, mm].some((n) => Number.isNaN(n))) return null;
  const mins = hh * 60 + mm;
  if (!ymd || ymd.length !== 10) return mins;
  const [Y, M, D] = ymd.split('-').map(Number);
  if ([Y, M, D].some((n) => Number.isNaN(n))) return mins;
  const dayDiff = Math.round((Date.UTC(y, mo - 1, day) - Date.UTC(Y, M - 1, D)) / 86400000);
  return mins + dayDiff * 1440;
};

// location key -> short label / css tone.
export const locationLabel = (loc) =>
  ({ office: 'Office', remote: 'Remote', unknown: 'Unknown' }[loc] || '—');

// A device's reported position. With exact (WiFi) location on, the backend adds
// lat/lng/accuracy_m and precise:true; otherwise it is the coarse IP/city guess.
// Returns null when location is off or nothing has resolved.
export const geoText = (loc) => {
  if (!loc) return null;
  const place = [loc.city, loc.country].filter(Boolean).join(', ');
  if (loc.precise && loc.lat != null && loc.lng != null) {
    const acc = loc.accuracy_m != null ? ` ±${Math.round(loc.accuracy_m)}m` : '';
    const coords = `${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)}${acc}`;
    // Prefer the reverse-geocoded address: coordinates are unreadable at a
    // glance. The raw fix is never lost - it stays in the hover text and behind
    // the Maps link. Falls back to coordinates while the address is resolving
    // (background lookup) or if the provider can't place the point.
    const addr = loc.address_short || loc.address || null;
    return {
      precise: true,
      approx: false,
      source: loc.source || loc.fix_source || 'device',
      // office/remote classification the backend attached from the office pins:
      // an office fix is shown by its admin name, a non-office by a Remote tag.
      place: loc.place || null,
      placeName: loc.place_name || null,
      // A location carried over from the device's CURRENT position because the
      // day predates per-day snapshots - true of history, never of new reports.
      current: !!loc.current,
      numeric: !addr,                       // tabular-nums suits digits, not prose
      text: addr || coords,
      sub: [addr && loc.address !== addr ? loc.address : null, coords, place]
        .filter(Boolean).join(' · ') || null,
      href: `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`,
    };
  }
  // Not a fix: a city derived from the public IP, which is the ISP's egress
  // point rather than anywhere a person is sitting. It is labelled approximate
  // wherever it appears, because a wrong city that LOOKS like an address is
  // worse than a blank.
  return place ? {
    precise: false, numeric: false, approx: true, source: loc.source || 'ip',
    current: !!loc.current, text: place,
    sub: 'Approximate \u2014 from the public IP, not a device fix',
    href: null,
  } : null;
};

// One CSV cell: neutralize spreadsheet formula injection (computer_name and WiFi
// SSID are set on the monitored laptop, so an employee can inject a formula),
// THEN quote for CSV. A leading = + - @ tab or CR makes Excel/LibreOffice treat
// the cell as a formula on open; prefixing a single quote defuses it.
export const csvCell = (c) => {
  let s = c == null ? '' : String(c);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Decimal hour -> "HH:MM" for axis labels.
export const hourLabel = (h) => {
  if (h == null || Number.isNaN(h)) return '';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// Percentage helper — a/b as an integer %, guarded against divide-by-zero.
export const pct = (a, b) => {
  const bb = Number(b) || 0;
  if (bb <= 0) return 0;
  return Math.round((Number(a) || 0) / bb * 100);
};

// YYYY-MM-DD minus N days, anchored on the GIVEN day rather than today - for a
// rolling window that ends on the report you are looking at.
export const ymdMinusDays = (ymd, days) => {
  if (!ymd || ymd.length !== 10) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  if ([y, m, d].some((v) => Number.isNaN(v))) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - (Number(days) || 0));
  const p2 = (v) => String(v).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p2(dt.getUTCMonth() + 1)}-${p2(dt.getUTCDate())}`;
};

// Today / N days ago as a YYYY-MM-DD string (local).
export const ymdOffset = (daysAgo) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
};
